import React from "react";
import { ROLE_LABELS } from "../../../constants/roles";

/**
 * Glyphes Boxicons par rôle — cohérents avec la sidebar (cf. Design
 * system §Iconographie : un glyphe sémantiquement distinct par rôle).
 */
const ROLE_ICONS: Record<string, string> = {
  ROLE_ADMIN: "bx-shield-quarter",
  ROLE_DOCTOR: "bx-plus-medical",
  ROLE_SECRETARY: "bx-clipboard",
  ROLE_PATIENT: "bx-user",
  ROLE_INSURER: "bx-shield-alt-2",
};

const FALLBACK_ROLE_ICON = "bx-user";

type Props = {
  role?: string | null;
  /**
   * `principal` : rôle porté par le discriminant `role` du `UserResponse`.
   * `additional` : rôle cumulé (`additionalRoles`), rendu en pastille plus
   * discrète pour rester visuellement secondaire.
   */
  variant?: "principal" | "additional";
  className?: string;
};

const VARIANT_CLASSES: Record<NonNullable<Props["variant"]>, string> = {
  principal: "border-slate-200 bg-slate-50 text-slate-700",
  additional: "border-emerald-100 bg-emerald-50 text-emerald-700",
};

/**
 * Pastille de rôle partagée (annuaire des utilisateurs, fiche personnel
 * médecin, drawer profil, en-tête du tableau de bord). Un utilisateur
 * affiche une pastille `principal` et autant de pastilles `additional`
 * qu'il porte de rôles cumulés.
 *
 * @example
 *   <RoleBadge role={getPrincipalRole(user)} />
 *   {getAdditionalRoles(user).map((role) => (
 *     <RoleBadge key={role} role={role} variant="additional" />
 *   ))}
 */
const RoleBadge = ({ role, variant = "principal", className = "" }: Props) => {
  if (!role) return null;

  const label = ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role;
  const icon = ROLE_ICONS[role] ?? FALLBACK_ROLE_ICON;

  return (
    <span
      title={variant === "additional" ? `Rôle additionnel : ${label}` : label}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium " +
        VARIANT_CLASSES[variant] +
        (className ? " " + className : "")
      }
    >
      <i className={`bx ${icon} text-emerald-600`} />
      {label}
    </span>
  );
};

export default RoleBadge;
